import { FCC } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Stack, Typography, Modal, styled, Box, Grid } from "@mui/material";
import { background, black, divider } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Button } from "@/components/elements/Button";
import { useEffect, useState, useMemo, useRef } from "react";
import { useGetPartnerSchema } from "@/features/talent/hooks/usePartnerSchema";
import { TPartnerForm } from "@/features/talent/types/partnerForm";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Form } from "@/components/elements/Form";
import { Select } from "@/components/elements/Select";
import { CityInput } from "@/components/elements/CityInput";
import { useGetNationalityList } from "@/api/services/global/master";
import { Textarea } from "@/components/elements/Textarea";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { createPartnerApi, updatePartnerApi } from "@/api/services/main/partner";
import { Checkbox } from "@/components/elements/Checkbox";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { showError } from "@/utils/error";
import { convertPartnerData } from "@/features/talent/utils/convertPartnerData";
import { JAPAN_NATIONALITY_ID } from "@/types/enum";
import { useEnum } from "@/api/services/global/enum";
import { IconClose } from "@/assets/icons";
import { IconBox } from "@/components/elements/IconBox";

type TFormPartnerModalProps = {
  modal: UseVisibleReturn;
  refetchPartners: () => void;
  refectTalentInfo?: () => void;
  refetchPartnerInfo?: () => void;
  defaultValues?: Partial<TPartnerForm> | null;
  onClose?: () => void;
};

export const FormPartnerModal: FCC<TFormPartnerModalProps> = ({
  modal,
  refetchPartners,
  refectTalentInfo,
  refetchPartnerInfo,
  defaultValues,
  onClose: onCloseHandler,
}) => {
  const [isSubmit, setIsSubmit] = useState(false);
  const [errorList, setErrorList] = useState<string[]>([]);
  const { partnerSchema } = useGetPartnerSchema();
  const { nationalityOptions } = useGetNationalityList();
  const { data: contactMethodOptions } = useEnum("EPartnerContactMethod");

  const methods = useForm<TPartnerForm>({
    defaultValues: {
      laborBureauStatus: false,
      sendingOrganization: false,
      prefecture: "",
      city: "",
      address: "",
      location: "",
      nationalityId: JAPAN_NATIONALITY_ID,
    },
    resolver: yupResolver(partnerSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  useEffect(() => {
    if (modal.visible) {
      if (defaultValues) {
        methods.reset(defaultValues);
      } else {
        methods.reset({
          laborBureauStatus: false,
          sendingOrganization: false,
          prefecture: "",
          city: "",
          address: "",
          location: "",
          nationalityId: JAPAN_NATIONALITY_ID,
        });
      }
    }
  }, [modal.visible, defaultValues, methods]);

  const { control, reset, setValue } = methods;
  const sendingOrganization = useWatch({ control, name: "sendingOrganization" });
  const laborBureauStatus = useWatch({ control, name: "laborBureauStatus" });
  const nationalityId = useWatch({ control, name: "nationalityId" });

  // Check if Japan is selected to show/hide address fields
  const isJapanSelected = useMemo(() => {
    return Number(nationalityId) === JAPAN_NATIONALITY_ID;
  }, [nationalityId]);

  const filteredNationalityOptions = useMemo(() => {
    if (sendingOrganization === true) {
      return nationalityOptions.filter((option) => Number(option.value) !== JAPAN_NATIONALITY_ID);
    }
    return nationalityOptions;
  }, [nationalityOptions, sendingOrganization]);

  const prevSendingOrganizationRef = useRef<boolean | undefined | null>(undefined);
  useEffect(() => {
    prevSendingOrganizationRef.current = sendingOrganization;
  }, []);

  useEffect(() => {
    if (sendingOrganization === false && prevSendingOrganizationRef.current === true) {
      const currentNationalityId = methods.getValues("nationalityId");
      if (currentNationalityId === undefined || currentNationalityId === null) {
        setValue("nationalityId", JAPAN_NATIONALITY_ID, { shouldValidate: true });
      }
    }

    if (sendingOrganization === true && prevSendingOrganizationRef.current === false) {
      if (defaultValues?.nationalityId) {
        setValue("nationalityId", Number(defaultValues.nationalityId), { shouldValidate: true });
      }
    }

    prevSendingOrganizationRef.current = sendingOrganization;
  }, [sendingOrganization, setValue, methods]);

  const locationInfo = useLocationInfo({
    methods,
    names: {
      zipCode: "zipCode",
      province: "prefecture",
      municipality: "city",
      address: "address",
    },
  });

  const onClose = () => {
    setIsSubmit(false);
    setErrorList([]);
    onCloseHandler?.();
    modal.close();
  };

  const onSubmit = async (data: TPartnerForm) => {
    try {
      setIsSubmit(true);
      setErrorList([]);

      if (data?.id) {
        const payload = convertPartnerData(data);
        await updatePartnerApi(payload.id!, payload);
        await refectTalentInfo?.();
        await refetchPartnerInfo?.();
      } else {
        const payload = convertPartnerData(data);
        await createPartnerApi(payload);
      }

      toast.success(API_SUCCESS_MESSAGE);
      reset();
      refetchPartners();
      onCloseHandler?.();
      modal.close();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <Modal open={modal.visible} hideBackdrop={false} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <ModalContainer>
        <Form methods={methods} onSubmit={(data) => onSubmit(data)} schema={partnerSchema}>
          {/* Header - Fixed */}
          <Stack
            direction="row"
            bgcolor={background.disable}
            height="56px"
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px", px: 3 }}
          >
            <Typography variant="body14Light" color={"#0B0C0D"}>
              {defaultValues ? "パートナー情報｜編集" : "パートナー情報 │ 新規登録"}
            </Typography>
            <IconBox onClick={onClose} sx={{ p: 0 }}>
              <IconClose color={black[80]} fontSize={24} />
            </IconBox>
          </Stack>

          {/* Scrollable Content */}
          <Box sx={{ overflowY: "auto", maxHeight: "calc(90vh - 56px - 72px)" }}>
            <Stack sx={{ p: 4, gap: "10px" }}>
              {!isSubmit && errorList.length > 0 && (
                <Box borderRadius={rounded.sm}>
                  <Typography variant="body14Light" color="#d32f2f">
                    {errorList.map((error, idx) => (
                      <p key={idx}>{error}</p>
                    ))}
                  </Typography>
                </Box>
              )}

              <Grid container columnSpacing={3} rowSpacing={2}>
                <Grid item xs={6}>
                  <FormField control={control} name="partnerName">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>
                <Grid item xs={6}>
                  <Stack>
                    <Typography variant="cap12" color={black[70]}>
                      送り出し機関情報
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Box mr={3}>
                        <FormField control={control} name="sendingOrganization" noLabel>
                          <Checkbox label="送り出し機関" checked={!!sendingOrganization} />
                        </FormField>
                      </Box>
                      <Box>
                        <FormField control={control} name="laborBureauStatus" noLabel>
                          <Checkbox label="労働局届出済" checked={!!laborBureauStatus} />
                        </FormField>
                      </Box>
                    </Box>
                    <Typography variant="cap12" color={black[70]} sx={{ mt: 1 }}>
                      送り出し機関として登録する場合は、チェックしてください。
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <FormField control={control} name="nationalityId">
                    <Select key={`nationality-select-${sendingOrganization}`} options={filteredNationalityOptions} placeholder="選択してください" />
                  </FormField>
                </Grid>

                {isJapanSelected && (
                  <>
                    <Grid item xs={4}>
                      <FormField control={control} name="zipCode">
                        <Input placeholder="入力してください" />
                      </FormField>
                    </Grid>

                    <Grid item xs={4}>
                      <FormField control={control} name="prefecture">
                        <Input placeholder="入力してください" readonly />
                      </FormField>
                    </Grid>

                    <Grid item xs={4}>
                      <CityInput name="city" control={control} zipCodeFieldName="zipCode" locationInfo={locationInfo} />
                    </Grid>

                    <Grid item xs={12}>
                      <FormField control={control} name="address">
                        <Input placeholder="入力してください" />
                      </FormField>
                    </Grid>
                  </>
                )}

                {nationalityId && !isJapanSelected && (
                  <Grid item xs={12}>
                    <FormField control={control} name="location">
                      <Input placeholder="入力してください" />
                    </FormField>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <FormField control={control} name="contactPerson">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>

                <Grid item xs={6}>
                  <FormField control={control} name="contactMethod">
                    <Select placeholder="選択してください" options={contactMethodOptions} />
                  </FormField>
                </Grid>

                <Grid item xs={4}>
                  <FormField control={control} name="phoneNumber">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>

                <Grid item xs={4}>
                  <FormField control={control} name="email">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>

                <Grid item xs={4}>
                  <FormField control={control} name="otherContacts">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>

                <Grid item xs={6}>
                  <FormField control={control} name="contractInfo">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>

                <Grid item xs={6}>
                  <FormField control={control} name="billingInfo">
                    <Input placeholder="入力してください" />
                  </FormField>
                </Grid>

                <Grid item xs={12}>
                  <FormField control={control} name="memo">
                    <Textarea placeholder="入力してください" />
                  </FormField>
                </Grid>
              </Grid>
            </Stack>
          </Box>

          {/* Footer - Fixed */}
          <Box>
            <hr color={divider.high} />
            <Stack direction="row" gap="16px" justifyContent="flex-end" p="16px 32px">
              <Button variant="ghost" onClick={onClose}>
                閉じる
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmit}>
                パートナー情報を保存
              </Button>
            </Stack>
          </Box>
        </Form>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  width: 920px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  outline: none;
  &:focus {
    outline: none;
  }
`;
